import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";

import openSocket from "../../services/socket-io";
import clsx from "clsx";

import {
  Paper,
  makeStyles,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText
} from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import TagCreateForm from "../TagCreateForm";

import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    marginRight: 0,
  },

  tagsWrapper: {
    display: "flex",
    flexWrap: "wrap",
    gap: 5,
    marginTop: 5
  }
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [openTagModal, setOpenTagModal] = useState(false);

  const isModalOpenRef = useRef(false);

  useEffect(() => {
    isModalOpenRef.current = openTagModal;
  }, [openTagModal]);

  // 🔹 cargar ticket
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/tickets/${ticketId}`);
        setTicket(data);
        setContact(data.contact);
        setTags(data.tags || []);
      } catch (err) {
        toastError(err);
      }
    };
    load();
  }, [ticketId]);

  // 🔹 cargar tags globales
  useEffect(() => {
    const load = async () => {
      const { data } = await api.get("/tags");
      setAllTags(data);
    };
    load();
  }, []);

  // 🔥 SOCKET
  useEffect(() => {
    const socket = openSocket();

    socket.emit("joinChatBox", ticketId);

    socket.on("ticket", (data) => {
      if (data.action === "update") {
        if (isModalOpenRef.current) return;

        setTicket(data.ticket);
        setTags(data.ticket.tags || []);
      }

      if (data.action === "delete") {
        history.push("/tickets");
      }
    });

    return () => socket.disconnect();
  }, [ticketId, history]);

  // 🔥 toggle tag
  const handleToggleTag = async (tag) => {
    const exists = tags.some(t => t.id === tag.id);

    try {
      let data;

      if (exists) {
        const res = await api.delete(`/tickets/${ticket.id}/tags/${tag.id}`);
        data = res.data;
      } else {
        const res = await api.post(`/tickets/${ticket.id}/tags`, {
          tagId: tag.id
        });
        data = res.data;
      }

      setTags(data.tags);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>

      <Paper
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >

        <TicketHeader>

          {/* 🔥 CLICK ABRE DRAWER */}
          <div onClick={() => setDrawerOpen(true)}>
            <TicketInfo contact={contact} ticket={ticket} />
          </div>

          <TicketActionButtons ticket={ticket} />
        </TicketHeader>

        {/* 🔥 TAGS */}
        <div className={classes.tagsWrapper}>
          {tags.map(tag => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              style={{
                backgroundColor: tag.color,
                color: "#fff"
              }}
              onDelete={() => handleToggleTag(tag)}
            />
          ))}

          <Button size="small" onClick={() => setOpenTagModal(true)}>
            + Etiqueta
          </Button>
        </div>

        <ReplyMessageProvider>
          <MessagesList ticketId={ticketId} />
          <MessageInput ticketStatus={ticket.status} />
        </ReplyMessageProvider>

      </Paper>

      {/* 🔥 DRAWER FIX REAL */}
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={() => setDrawerOpen(false)}
        contact={contact}
      />

      {/* 🔥 MODAL TAGS */}
      <Dialog open={openTagModal} onClose={() => setOpenTagModal(false)}>
        <DialogTitle>Etiquetas</DialogTitle>

        <DialogContent>

          <List>
            {allTags.map(tag => {
              const checked = tags.some(t => t.id === tag.id);

              return (
                <ListItem
                  key={tag.id}
                  button
                  onClick={() => handleToggleTag(tag)}
                >
                  <Checkbox checked={checked} />
                  <ListItemText primary={tag.name} />
                </ListItem>
              );
            })}
          </List>

          <TagCreateForm
            onCreated={async (newTag) => {
              setAllTags(prev => [...prev, newTag]);

              const { data } = await api.post(
                `/tickets/${ticket.id}/tags`,
                { tagId: newTag.id }
              );

              setTags(data.tags);
            }}
          />

        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Ticket;